import React, { useMemo } from "react";

export default function ShelfGrid({ items, meta, onItemClick, onEmptyClick }) {
  const cols = Array.from({ length: meta.max_col || 30 }, (_, i) => i + 1);
  const rows = meta.rows || ["A","B","C","D","E","F","G","H","I","J"];
  const cellMap = useMemo(() => {
    const m = {};
    for (const it of items) {
      if (!it.shelf_row || !it.shelf_col) continue;
      const k = `${it.shelf_row}-${it.shelf_col}`;
      if (!m[k]) m[k] = [];
      m[k].push(it);
    }
    return m;
  }, [items]);
  const unplaced = items.filter((it) => !it.shelf_row || !it.shelf_col);

  return (
    <div className="space-y-3" data-testid="shelf-grid">
      <div className="overflow-x-auto pb-1">
        <table className="border-collapse" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              <th className="w-8 h-7 text-[10px] text-zinc-400 sticky left-0 bg-white z-10"></th>
              {cols.map((c) => (
                <th key={c} className="w-7 h-7 text-[9px] text-zinc-400 font-mono text-center">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r}>
                <td className="w-8 h-7 text-[10px] text-zinc-500 font-mono font-bold text-center sticky left-0 bg-white z-10">{r}</td>
                {cols.map((c) => {
                  const itemsHere = cellMap[`${r}-${c}`] || [];
                  const it = itemsHere[0];
                  const more = itemsHere.length - 1;
                  const colorObj = it && it.color ? meta.colors.find((x) => x.code === it.color) : null;
                  const defect = it?.status === "defect";
                  let bg = "transparent";
                  let border = "1px solid #E4E4E7";
                  if (it) {
                    bg = colorObj?.hex || (defect ? "#EF4444" : "#10B981");
                    border = defect ? "2px solid #EF4444" : `1px solid ${colorObj?.hex || "#10B981"}`;
                  }
                  const tooltip = it
                    ? `${it.name}${it.quantity ? ` (${it.quantity}${it.unit ? " " + it.unit : ""})` : ""}${colorObj ? ` · ${colorObj.label}` : ""}${defect ? " · DEFECT" : ""}${more > 0 ? ` · +${more}` : ""}`
                    : (onEmptyClick ? `${r}${c} · click pentru plasare rapidă` : `${r}${c} · gol`);
                  const handleClick = it ? () => onItemClick(it) : (onEmptyClick ? () => onEmptyClick(r, c) : undefined);
                  const emptyClickable = !it && !!onEmptyClick;
                  return (
                    <td key={c} className="p-0">
                      <button
                        onClick={handleClick}
                        title={tooltip}
                        disabled={!it && !onEmptyClick}
                        data-testid={`shelf-cell-${r}${c}`}
                        className={`w-7 h-7 m-[1px] rounded-sm transition-transform relative disabled:cursor-default ${it || emptyClickable ? "hover:scale-110 hover:z-20" : ""} ${emptyClickable ? "hover:!opacity-100 hover:!border-[var(--primary)] hover:bg-zinc-50" : ""}`}
                        style={{ background: bg, border, opacity: it ? 1 : (emptyClickable ? 0.5 : 0.4) }}
                      >
                        {it && more > 0 && (
                          <span className="absolute -top-1 -right-1 text-[8px] bg-zinc-900 text-white rounded-full w-3 h-3 grid place-items-center font-bold">{more + 1}</span>
                        )}
                        {defect && <span className="absolute inset-0 grid place-items-center text-white text-[10px] font-bold">!</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-[10px] text-zinc-500 flex-wrap">
        <span>Legendă:</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#10B981]"/>funcțional</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#EF4444] grid place-items-center text-white text-[8px] font-bold">!</span>defect</span>
        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm border border-zinc-200 opacity-40"/>celulă goală</span>
        {onEmptyClick && <span className="text-[var(--primary)]">· click pe goală = plasare rapidă</span>}
        <span>· culoarea celulei = culoarea echipei</span>
      </div>

      {unplaced.length > 0 && (
        <div className="border-t border-dashed border-[var(--border)] pt-3">
          <div className="text-[11px] text-zinc-500 mb-2">{unplaced.length} produs(e) fără raft atribuit:</div>
          <div className="flex flex-wrap gap-1.5">
            {unplaced.map((it) => (
              <button
                key={it.id}
                onClick={() => onItemClick(it)}
                className="text-[10px] px-2 py-1 rounded-md border border-dashed border-zinc-300 hover:border-[var(--primary)] hover:bg-zinc-50"
                data-testid={`unplaced-${it.id}`}
              >
                {it.name}{it.quantity > 1 ? ` (${it.quantity})` : ""}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
